import {
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
  createAction,
} from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { Reducer } from "redux";
import { reducers } from "./reducers";
import { privateApi } from "./services/privateApi";
import { publicApi } from "./services/publicApi";
import { createErrorDetailsParamsProps } from "src/shared/models/component/base.model";
import { proofupApi } from "./services/proofupApi";
import { isPropertyInactivePayload } from "./propertyInactive";
import { securityOptions } from "src/shared/config/storeConfig";
import { stashAuthReturnTo } from "src/shared/utilities/authRedirect";

export const RESET_STATE_ACTION_TYPE = "resetState";

export const resetStateAction = createAction(RESET_STATE_ACTION_TYPE, () => {
  // Skip redirect on public / error routes (avoids /error/401 loops).
  const path = window.location.pathname;
  const skipRedirect =
    path === "/" ||
    path === "/unverified-email" ||
    path === "/verified-email" ||
    path === "/verify-login" ||
    path === "/invitation" ||
    path.includes("/invitation") ||
    path === "/401" ||
    path === "/error/401" ||
    path.startsWith("/error/") ||
    path === "/register" ||
    path === "/404";

  if (typeof window !== "undefined" && !skipRedirect) {
    // Stash so Error401 Sign In can returnTo the affordable (or other) page.
    stashAuthReturnTo(`${window.location.pathname}${window.location.search}`);
    window.location.href = "/error/401";
  }

  return { payload: null };
});

export const resetStateAction404 = createAction(
  RESET_STATE_ACTION_TYPE,
  (errorDetails: createErrorDetailsParamsProps) => {
    // Error checking is unavailable for following routes
    const errorNoNeedToCheck = window.location.pathname === "/";

    // check error and passthrohgh the search params
    if (typeof window !== "undefined" && errorNoNeedToCheck) {
      window.location.href = `/error/api/404/?error=${JSON.stringify(
        errorDetails
      )}`;
    }

    return { payload: null };
  }
);

// Redirect residents whose property has been dispositioned to a dedicated
// full-screen message. Mirrors the 401/404 redirect pattern; guarded so it does
// not loop once already on the screen.
export const resetStateActionPropertyInactive = createAction(
  RESET_STATE_ACTION_TYPE,
  () => {
    if (
      typeof window !== "undefined" &&
      window.location.pathname !== "/error/property-inactive"
    ) {
      window.location.href = "/error/property-inactive";
    }

    return { payload: null };
  }
);

// set middleware for error handling
export const unauthenticatedMiddleware: Middleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    // Endpoints whose failures must NEVER trigger the global reset/redirect.
    // The Rex Support chatbot token (getSupportContext) is best-effort and
    // mounted app-wide via RexSupportWidget; it legitimately 404s for anyone
    // without a resident profile/active lease (e.g. applicants). Letting that
    // 404 reach resetStateAction404 wipes the whole Redux store and redirects
    // to /error/api/404 — blocking the entire app. The widget handles its own
    // failure locally, so we short-circuit here.
    if (
      isRejectedWithValue(action) &&
      action.meta?.arg?.endpointName === "getSupportContext"
    ) {
      return next(action);
    }

    // Correction / login-as mode rides a short-lived ops grant, not an Auth0
    // session. A 401 here just means the grant ended — it must NOT wipe the
    // store and redirect to /error/401.
    if (isRejectedWithValue(action) && securityOptions.hasOpsGrant()) {
      return next(action);
    }

    if (isRejectedWithValue(action) && action.payload.status === 401) {
      dispatch(resetStateAction());
    } else if (
      isRejectedWithValue(action) &&
      isPropertyInactivePayload(action.payload)
    ) {
      dispatch(resetStateActionPropertyInactive());
    } else if (isRejectedWithValue(action) && action.payload.status === 404) {
      // Create object to define the api error details ___
      const createErrorDetailsParam: createErrorDetailsParamsProps = {
        errorStatus: action.payload.status ?? "404",
        errorMessage:
          action.payload?.data?.err ??
          action.payload?.data?.error ??
          "404 api Error",
        targetAPI:
          action.meta.baseQueryMeta.request.url ??
          action.meta.baseQueryMeta.response.url ??
          "Unable to fetch the URL",
        endpointName: action.meta.arg.endpointName ?? "",
        method: action.meta.baseQueryMeta.request.method ?? "",
      };

      // dispatch the action ___
      dispatch(resetStateAction404(createErrorDetailsParam));
    }

    return next(action);
  };

const combinedReducer = combineReducers<typeof reducers>(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
  if (action.type === RESET_STATE_ACTION_TYPE) {
    state = {} as RootState;
  }

  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: reducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      privateApi.middleware,
      publicApi.middleware,
      proofupApi.middleware,
      unauthenticatedMiddleware
    ),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof combinedReducer>;
export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
